import React, { useState, useEffect } from 'react';
import {
  Table,
  Typography,
  Tooltip,
  Button,
  Row,
  Col,
  Popconfirm,
  message,
  Card,
  Modal,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ApiService from '../Components/apiService';
import Cookies from 'js-cookie';
import CreateGroup from './CreateGroup'; // Import the CreateGroup component
import { BASE_URL, DateFormat, DateTimeFormat } from '../Components/Constant';
import dayjs from 'dayjs';
import { convertDateToUserTimezone } from '../Components/Utility';
import { useSelector } from 'react-redux';

const { Title } = Typography;

const Groups = () => {
  const [allGroups, setAllGroups] = useState([]);
  const [groupDetails, setGroupDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [users, setUsers] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null); // To track the group being edited
  const [isCreatingOrEditing, setIsCreatingOrEditing] = useState(false); // To toggle between list and form view
  const [pageSize, setPageSize] = useState(10);
  const security = useSelector((state) => state.data.items[0]);

  const userId = Cookies.get('userId');

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const apiService = new ApiService(
        `${BASE_URL}/get_all_groups/`,
        { 'Content-Type': 'application/json' },
        'GET'
      );
      const response = await apiService.makeCall();
      setAllGroups(response.groups || []); // Fallback to empty array
    } catch (error) {
      console.error('Error fetching groups:', error);
      message.error('Failed to fetch groups. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const apiService = new ApiService(
        `${BASE_URL}/fetch_records/User`,
        { 'Content-Type': 'application/json' },
        'GET'
      );
      const response = await apiService.makeCall();
      setUsers(response?.records);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreate = () => {
    if (security?.system_settings?.user_group?.create === false) {
      message.error('You do not have permission to create ');
      return;
    }
    fetchUsers();
    setEditingGroup(null); // Clear editingGroup to indicate create mode
    setIsCreatingOrEditing(true);
  };

  const handleEdit = (group) => {
    if (security?.system_settings?.user_group?.edit === false) {
      message.error('You do not have permission to edit ');
      return;
    }
    fetchUsers();
    setEditingGroup(group); // Set the group to be edited
    setIsCreatingOrEditing(true);
  };

  const handleDelete = async (groupId) => {
    if (security?.system_settings?.user_group?.delete === false) {
      message.error('You do not have permission to delete ');
      return;
    }
    try {
      const apiService = new ApiService(
        `${BASE_URL}/delete_group/${groupId}`,
        { 'Content-Type': 'application/json' },
        'DELETE'
      );
      const response = await apiService.makeCall();
      if (response.message) {
        message.success('Group deleted successfully');
        fetchGroups(); // Refresh groups list
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      message.error('Failed to delete group');
    }
  };

  const handleCancel = () => {
    setIsCreatingOrEditing(false); // Show the table again
  };

  const handleSave = async (groupData) => {
    const { name, apiName, description, user_ids } = groupData;
    const body = {
      name: name.trim(),
      apiName: apiName?.trim() || null,
      description: description || '',
      user_ids: user_ids || [],
    };

    try {
      if (editingGroup?._id) {
        // Update existing group
        const apiService = new ApiService(
          `${BASE_URL}/edit_group/${editingGroup._id}`,
          { 'Content-Type': 'application/json' },
          'POST',
          body
        );
        await apiService.makeCall();
        message.success('Group updated successfully');
      } else {
        // Create a new group
        const apiService = new ApiService(
          `${BASE_URL}/create_group`,
          { 'Content-Type': 'application/json' },
          'POST',
          body
        );
        const response = await apiService.makeCall();
        message.success('Group created successfully');
        setAllGroups((prevGroups) => [...prevGroups, response.newGroup]);
      }

      // Refresh groups and reset states
      fetchGroups();
      setEditingGroup(null);
      setIsCreatingOrEditing(false);
    } catch (error) {
      console.error('Error saving group:', error);
      message.error('Failed to save group. Please try again.');
    }
  };


  const columns = [
    
    {
      title: 'Name',
      dataIndex: 'apiName',
      key: 'apiName',
      sorter: (a, b) => a.apiName.localeCompare(b.apiName),
      render: (text, record) => (
        <a onClick={() => handleEdit(record)}>{text}</a>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 100,

    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      // width: 100,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (text) => (
        text && dayjs(text).isValid()
          ? convertDateToUserTimezone(text, DateTimeFormat)
          : ''
      ),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      // width: 100,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (text) => (
        text && dayjs(text).isValid()
          ? convertDateToUserTimezone(text, DateTimeFormat)
          : ''
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <>
          <Tooltip title="Edit">
            <EditOutlined
              onClick={() => handleEdit(record)}
              style={{ marginRight: 5, fontSize: '12px', cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this item?"
              onConfirm={() => handleDelete(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined
                style={{ color: 'red', fontSize: '12px', cursor: 'pointer' }}
              />
            </Popconfirm>
          </Tooltip>
        </>
      ),
    },
  ];
  useEffect(() => {
    fetchGroups();
  }, [userId]);

  return (
    <Card>
      {isCreatingOrEditing ? (
        <CreateGroup
          onSave={handleSave}
          onCancel={handleCancel}
          fetchUsers={fetchUsers}
          isEdit={!!editingGroup}
          editingGroup={editingGroup}
          userList={users}
        />
      ) : (
        <>
          <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                Groups
              </Title>
            </Col>
            <Col>
              <Button type="primary" onClick={handleCreate}>
                Create Group
              </Button>
            </Col>
          </Row>
          <Table
            columns={columns}
            dataSource={allGroups}
            rowKey="_id"
            loading={loading}
            scroll={{ x: 'max-content', y: 550 }}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPageSize(pageSize);
              },
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </>
      )}
    </Card>
  );
};

export default Groups;
